import { CSSProperties, FC, ReactNode, useEffect, useState } from 'react';
import clsx from 'clsx';
import { useTheme } from '../../../hooks/useTheme';
import Dismiss from './components/Dismiss';
import styles from './Alert.module.css';

const REMOVAL_OFFSET = 10; // 10ms

export interface BlockAlertProps {
  children: ReactNode;
  show?: boolean;
  dismissible?: boolean;
  dismiss?: ReactNode;
  dismissOnClick?: () => void;
  timeout?: number;
  // componentThemeOverrides for broadcastBanner
  styleAlertOverrides?: CSSProperties;
}

const BlockAlert: FC<BlockAlertProps> = ({
  children,
  show = false,
  dismissible = false,
  dismiss,
  dismissOnClick,
  timeout = 300, // duration
  styleAlertOverrides,
}) => {
  const {
    alertNoneNone: alertContainerStyle,
    buttonsDismissNone: buttonsDismissStyle,
  } = useTheme();

  const [isFadingOut, setIsFadingOut] = useState(false);
  const shouldAlertRender = show || isFadingOut;

  useEffect(() => {
    if (!show) {
      setIsFadingOut(true);
    }
  }, [show]);

  useEffect(() => {
    let fadeOutTimer: ReturnType<typeof setTimeout>;

    if (isFadingOut) {
      const _timeout = timeout ? timeout + REMOVAL_OFFSET : REMOVAL_OFFSET;

      fadeOutTimer = setTimeout(() => {
        setIsFadingOut(false);
      }, _timeout);
    }

    return () => {
      if (fadeOutTimer) {
        clearTimeout(fadeOutTimer);
      }
    };
  }, [isFadingOut, timeout]);

  const style: CSSProperties = {
    opacity: show ? 1 : 0,
    animationDuration: timeout ? `${timeout}ms` : '0ms',
    animationName: isFadingOut ? styles.fadeOut : 'none',
  };

  if (!shouldAlertRender) {
    return null;
  }

  const alertClasses = clsx({
    [styles.alert]: true,
  });

  return (
    <div className={styles.container} style={style}>
      {dismissible && (
        <div className={styles.dismiss}>
          {dismiss || (
            <Dismiss s={buttonsDismissStyle} onClick={dismissOnClick} />
          )}
        </div>
      )}
      <div
        className={alertClasses}
        role="alert"
        style={styleAlertOverrides ?? alertContainerStyle}
      >
        {children}
      </div>
    </div>
  );
};

export default BlockAlert;
